import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeModalState, deleteComment, fetchImageComments } from '../../../modules/actions';
import {
  getCommentsThread,
  getIsCommentsFetching,
  getModalData,
  getModalsStates
} from '../../../modules/selectors';
import { IComment } from '../../../modules/types/comments';

export const useImageCommentsModal = () => {
  const dispatch = useDispatch();
  const { imageCommentsModal } = useSelector(getModalsStates);
  const modalData = useSelector(getModalData);
  const getThread = useSelector(getCommentsThread);
  const isFetching = useSelector(getIsCommentsFetching);
  const [pendingDelete, setPendingDelete] = useState<IComment | null>(null);

  const imageId = modalData?.commentsImageId;
  const collectionId = modalData?.commentsCollectionId;
  const isOpen = Boolean(imageCommentsModal);

  // getThread returns a stable [] for an unknown image, so this memo holds its
  // identity across renders and keeps the row list from re-rendering.
  const thread = useMemo(() => (imageId ? getThread(imageId) : []), [getThread, imageId]);

  useEffect(() => {
    if (isOpen && imageId && collectionId) {
      dispatch(fetchImageComments({ collection_id: collectionId, image_id: imageId }));
    }
  }, [isOpen, imageId, collectionId]); // eslint-disable-line

  const closeModal = useCallback(() => {
    setPendingDelete(null);
    dispatch(changeModalState({ key: 'imageCommentsModal', state: false }));
  }, [dispatch]);

  const requestDelete = useCallback((comment: IComment) => setPendingDelete(comment), []);
  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;

    dispatch(deleteComment({ commentId: pendingDelete._id, imageId }));
    setPendingDelete(null);
  }, [dispatch, pendingDelete, imageId]);

  return {
    isOpen,
    thread,
    isFetching,
    pendingDelete,
    closeModal,
    requestDelete,
    cancelDelete,
    confirmDelete
  };
};
